import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    Tag,
    Typography,
    Input,
    Select,
    message,
    Statistic,
    Row,
    Col,
    Descriptions,
    Badge,
    Spin,
    Collapse,
    Form,
    Tooltip
} from 'antd';
import {
    FileTextOutlined,
    SearchOutlined,
    EyeOutlined,
    UserOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    BarChartOutlined,
    FilterOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import disputeService from '../../../services/disputeService';
import adminService from '../../../services/adminService';
import DashboardLayout from '../../../components/Dashboard/DashboardLayout';
import PageHeader from '../../../components/Common/PageHeader';
import { toTitleCase } from '../../../utils/format';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const AdminDisputes = () => {
    const navigate = useNavigate();
    const [disputes, setDisputes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statisticsLoading, setStatisticsLoading] = useState(true);
    const [statistics, setStatistics] = useState(null);
    const [admins, setAdmins] = useState([]);
    const [filters, setFilters] = useState({
        status: null,
        priority: null,
        assigned_admin_id: null,
        search: ''
    });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0
    });
    const [searchForm] = Form.useForm();
    useEffect(() => {
        loadDisputes();
        loadStatistics();
        loadAdmins();
    }, [filters, pagination.current]);

    const loadDisputes = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.current,
                per_page: pagination.pageSize,
                ...filters
            };
            const response = await disputeService.adminGetDisputes(params);
            if (response.status === 'success') {
                setDisputes(response.data);
                if (response.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        total: response.pagination.total
                    }));
                }
            }
        } catch (error) {
            message.error('Failed to load disputes');
        } finally {
            setLoading(false);
        }
    };

    const loadStatistics = async () => {
        try {
            setStatisticsLoading(true);
            const response = await disputeService.adminGetStatistics();
            if (response.status === 'success') {
                setStatistics(response.data);
            }
        } catch (error) {
            console.error('Failed to load statistics');
        } finally {
            setStatisticsLoading(false);
        }
    };


    const loadAdmins = async () => {
        try {
            const response = await adminService.getUsers({ role: 'admin' });
            if (response.status === 'success') {
                setAdmins(response.data?.data || []);
            }
        } catch (error) {
            console.error('Failed to load admins');
        }
    };

    const handleViewDispute = (dispute) => {
        navigate(`/disputes/${dispute.dispute_number.toLowerCase()}`);
    };

    const handleSearch = (values) => {
        const newFilters = {
            search: values.search || '',
            status: values.status || null,
            priority: values.priority || null,
            assigned_admin_id: values.assigned_admin_id || null
        };
        setFilters(newFilters);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleResetFilters = () => {
        searchForm.resetFields();
        const defaultFilters = {
            search: '',
            status: null,
            priority: null,
            assigned_admin_id: null
        };
        setFilters(defaultFilters);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            under_review: 'blue',
            awaiting_response: 'purple',
            resolved: 'green',
            closed: 'default',
            cancelled: 'red'
        };
        return colors[status] || 'default';
    };

    const columns = [
        {
            title: 'Dispute Number',
            dataIndex: 'dispute_number',
            key: 'dispute_number',
            width: 140,
            fixed: 'left',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Subject',
            dataIndex: 'subject',
            key: 'subject',
            width: 250,
            ellipsis: true
        },
        {
            title: 'Parties',
            key: 'parties',
            width: 220,
            render: (_, record) => {
                const getFilerName = () => {
                    if (record.filer?.name) return record.filer.name;
                    const firstName = record.filer?.first_name || '';
                    const lastName = record.filer?.last_name || '';
                    const fullName = `${firstName} ${lastName}`.trim();
                    return fullName || record.filer?.email || 'Unknown';
                };
                
                const getRespondentName = () => {
                    if (record.respondent?.name) return record.respondent.name;
                    const firstName = record.respondent?.first_name || '';
                    const lastName = record.respondent?.last_name || '';
                    const fullName = `${firstName} ${lastName}`.trim();
                    return fullName || record.respondent?.email || 'Unknown';
                };
                
                const filerName = getFilerName();
                const respondentName = getRespondentName();
                
                return (
                    <Tooltip 
                        title={`Filed by: ${filerName.includes('@') || filerName === 'Unknown' ? filerName : toTitleCase(filerName)} | Against: ${respondentName.includes('@') || respondentName === 'Unknown' ? respondentName : toTitleCase(respondentName)}`}
                        placement="topLeft"
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <Text 
                                strong
                                style={{ 
                                    color: '#1890ff',
                                    fontSize: '13px'
                                }}
                            >
                                {filerName.includes('@') || filerName === 'Unknown' ? filerName : toTitleCase(filerName)}
                            </Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>vs</Text>
                            <Text 
                                style={{ 
                                    color: '#262626',
                                    fontSize: '13px'
                                }}
                            >
                                {respondentName.includes('@') || respondentName === 'Unknown' ? respondentName : toTitleCase(respondentName)}
                            </Text>
                        </div>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status.replace('_', ' ').toUpperCase()}
                </Tag>
            )
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            width: 100,
            render: (priority) => {
                const colors = {
                    low: 'default',
                    medium: 'blue',
                    high: 'orange',
                    urgent: 'red'
                };
                return (
                    <Tag color={colors[priority]}>
                        {priority.toUpperCase()}
                    </Tag>
                );
            }
        },
        {
            title: 'Assigned Admin',
            key: 'assigned_admin',
            width: 150,
            ellipsis: true,
            render: (_, record) => {
                const adminName = record.assigned_admin?.name || 
                    (record.assigned_admin?.first_name && record.assigned_admin?.last_name 
                        ? `${record.assigned_admin.first_name} ${record.assigned_admin.last_name}`.trim()
                        : null) ||
                    null;
                const displayAdminName = adminName ? (adminName.includes('@') || adminName === 'Unknown' ? adminName : toTitleCase(adminName)) : null;
                return displayAdminName ? (
                    <Tooltip title={displayAdminName}>
                        <Text>{displayAdminName}</Text>
                    </Tooltip>
                ) : (
                    <Text type="secondary">Unassigned</Text>
                );
            }
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 130,
            render: (date) => moment(date).format('MMM DD, YYYY')
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 100,
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDispute(record)}
                    >
                        {/* View */}
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <DashboardLayout>
            <PageHeader
                title="Dispute Management"
                description="Manage and resolve disputes between buyers and sellers"
            />

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Spin spinning={statisticsLoading}>
                        <Card>
                            <Statistic
                                title="Total Disputes"
                                value={statistics?.total || 0}
                                prefix={<FileTextOutlined />}
                            />
                        </Card>
                    </Spin>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Spin spinning={statisticsLoading}>
                        <Card>
                            <Statistic
                                title="Pending"
                                value={statistics?.pending || 0}
                                valueStyle={{ color: '#fa8c16' }}
                                prefix={<ClockCircleOutlined />}
                            />
                        </Card>
                    </Spin>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Spin spinning={statisticsLoading}>
                        <Card>
                            <Statistic
                                title="Under Review"
                                value={statistics?.under_review || 0}
                                valueStyle={{ color: '#1890ff' }}
                                prefix={<ExclamationCircleOutlined />}
                            />
                        </Card>
                    </Spin>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Spin spinning={statisticsLoading}>
                        <Card>
                            <Statistic
                                title="Resolved"
                                value={statistics?.resolved || 0}
                                valueStyle={{ color: '#52c41a' }}
                                prefix={<CheckCircleOutlined />}
                            />
                        </Card>
                    </Spin>
                </Col>
            </Row>

            <Card style={{ marginBottom: 16 }}>
                <Collapse defaultActiveKey={['1']} ghost>
                    <Panel 
                        header={
                            <Space>
                                <FilterOutlined />
                                <span>Advanced Search</span>
                            </Space>
                        } 
                        key="1"
                    >
                        <Form
                            form={searchForm}
                            layout="vertical"
                            onFinish={handleSearch}
                            initialValues={{
                                search: '',
                                status: null,
                                priority: null,
                                assigned_admin_id: null
                            }}
                        >
                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="search"
                                        label="Search"
                                    >
                                        <Input 
                                            prefix={<SearchOutlined />}
                                            placeholder="Search disputes..."
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="status"
                                        label="Status"
                                    >
                                        <Select placeholder="Filter by Status" allowClear>
                                            <Option value="pending">Pending</Option>
                                            <Option value="under_review">Under Review</Option>
                                            <Option value="awaiting_response">Awaiting Response</Option>
                                            <Option value="resolved">Resolved</Option>
                                            <Option value="closed">Closed</Option>
                                            <Option value="cancelled">Cancelled</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="priority"
                                        label="Priority"
                                    >
                                        <Select placeholder="Filter by Priority" allowClear>
                                            <Option value="low">Low</Option>
                                            <Option value="medium">Medium</Option>
                                            <Option value="high">High</Option>
                                            <Option value="urgent">Urgent</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="assigned_admin_id"
                                        label="Assigned Admin"
                                    >
                                        <Select placeholder="Filter by Admin" allowClear>
                                            {admins.map(admin => (
                                                <Option key={admin.id} value={admin.id}>
                                                    {admin.first_name} {admin.last_name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item>
                                <Space>
                                    <Button
                                        type="primary" 
                                        htmlType="submit"
                                        icon={<SearchOutlined />}
                                    >
                                        Search
                                    </Button>
                                    <Button 
                                        onClick={handleResetFilters}
                                        icon={<ReloadOutlined />}
                                    >
                                        Reset
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Panel>
                </Collapse>
            </Card>

 
                <Table
                    scroll={{ x: 1240 }}
                    columns={columns}
                    dataSource={disputes}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} disputes`,
                        onChange: (page, pageSize) => {
                            setPagination({ ...pagination, current: page, pageSize });
                        }
                    }}
                />

        </DashboardLayout>
    );
};

export default AdminDisputes;

